import { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Brand from "../components/Brand";
import useAuth from "../hooks/useAuth";
import { useToast } from "../context/ToastContext";
import { LEGAL_LIBRARY } from "../constants/legal";
import { version } from "../version";

const MainLogin = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 clamp(12px, 4vw, 24px);
  min-height: 100vh;
`;

const LoginContainer = styled.div`
  width: 100%;
  margin-top: clamp(12px, 4vw, 32px);
  text-align: center;
`;

const LogoWrapper = styled.div`
  margin-bottom: clamp(20px, 4vw, 30px);

  > div {
    min-height: clamp(104px, 16vw, 132px);
  }
`;

const LoginHeader = styled.div`
  width: min(80%, 360px);
  margin: clamp(22px, 4vw, 30px) auto;
  text-align: center;

  h1 {
    font-size: clamp(24px, 3vw, 28px);
    font-weight: 700;
    color: #000;
    margin-bottom: 10px;
  }

  p {
    font-size: clamp(15px, 2vw, 17px);
    line-height: 1.4;
    color: #555;
    margin: 0;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const InputField = styled.input`
  width: 100%;
  border-radius: 6px;
  height: clamp(54px, 7vw, 60px);
  border: 1px solid #afafaf;
  margin-bottom: clamp(16px, 3vw, 25px);
  padding: 0 clamp(16px, 3vw, 20px);
  font-size: 16px;
  outline: none;
`;

const ButtonControls = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 10px;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ButtonRegister = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    border-radius: 100px;
    color: #111;
    background-color: #f3f3f3;
    border: 1px solid #f3f3f3;
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.25s ease;

    &:hover {
      color: white;
      background-color: #000;
      border-color: #000;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`;

const ButtonLogin = styled.div`
  display: flex;
  justify-content: flex-end;

  button {
    border-radius: 100px;
    color: white;
    background-color: #ff0102;
    border: 1px solid #ff0102;
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.25s ease;

    &:hover {
      background-color: #e00000;
    }

    &:disabled {
      cursor: wait;
      opacity: 0.7;
    }
  }
`;

const LegalLinksRow = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 8px 12px;
  }
`;

const LegalAnchor = styled.a`
  color: #555;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: #000;
    text-decoration: underline;
  }
`;

const Version = styled.div`
  margin-top: clamp(28px, 7vw, 60px);
  text-align: center;
  color: #afafaf;
  font-size: 14px;
`;

const Brands = styled.div`
  padding: clamp(18px, 4vw, 25px) clamp(0px, 8vw, 50px) 24px;
`;

const LoginPage = (props) => {
  const { login } = useAuth();
  const toast = useToast();
  const history = useHistory();
  const [referrer, setReferrer] = useState("/");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const requestedReferrer = props?.location?.state?.referrer;
    const canReturnToReferrer =
      typeof requestedReferrer === "string" &&
      requestedReferrer !== "/login" &&
      (requestedReferrer.startsWith("/almacen") || requestedReferrer.startsWith("/w/"));

    if (canReturnToReferrer) {
      setReferrer(requestedReferrer);
    }
  }, [props?.location?.state?.referrer]);

  const handleLogin = async () => {
    if (!email) return toast.error("El campo email es requerido");
    if (!password) return toast.error("El campo password es requerido");

    try {
      setIsLoading(true);
      await login({ email, password });
      // Forzar recarga completa para limpiar todo estado residual de sesiones anteriores
      window.location.href = referrer;
    } catch (error) {
      toast.error(error?.message || "Credenciales no validas");
      setIsLoading(false);
    }
  };

  const handleRegister = () => {
    if (isLoading) return;
    history.push("/signup/almacen");
  };

  return (
    <Fragment>
      <MainLogin>
        <LoginContainer>
          <LogoWrapper>
            <Brand type="almacen" width="300" height="64" />
          </LogoWrapper>

          <LoginHeader>
            <h1>Inicia sesión</h1>
            <p>Si ya tienes una cuenta Almacenia, inicia sesión más abajo.</p>
          </LoginHeader>

          <div>
            <InputField
              type="email"
              placeholder="Email"
              disabled={isLoading}
              value={email}
              onChange={(event) => setEmail(event.target.value.toLowerCase())}
            />
            <InputField
              type="password"
              placeholder="Password"
              disabled={isLoading}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !isLoading) handleLogin();
              }}
            />
          </div>

          <ButtonControls>
            <ButtonRegister>
              <button onClick={handleRegister} disabled={isLoading}>
                Registrarse
              </button>
            </ButtonRegister>
            <ButtonLogin>
              <button onClick={handleLogin} disabled={isLoading}>
                {isLoading ? "Cargando..." : "Ingresar"}
              </button>
            </ButtonLogin>
          </ButtonControls>

          <LegalLinksRow>
            {LEGAL_LIBRARY.map((item) => (
              <LegalAnchor key={item.href} href={item.href} target="_blank" rel="noreferrer">
                {item.label}
              </LegalAnchor>
            ))}
          </LegalLinksRow>

          <Brands>
            <Version>{version}</Version>
          </Brands>
        </LoginContainer>
      </MainLogin>
    </Fragment>
  );
};

export default LoginPage;
